/**
 * Event-Driven Automation Engine
 * 
 * ⚠️  INTERNAL MODULE — Do not import directly from application code.
 *     Use `executionEngine.ts` as the single execution entry point.
 *     Only AutomationControlPanel (UI config) and automationRunner (delegated)
 *     may import this module directly.
 * 
 * MODULE OWNERSHIP: Automation domain (see moduleOwnership.ts)
 * RESPONSIBILITY: Rule definitions, condition evaluation, action execution.
 * 
 * ENFORCEMENT: Guarded by INTERNAL_MODULES in moduleOwnership.ts.
 * Unauthorized imports will throw in DEV and warn in PROD.
 */

/** @internal — Marker for architecture enforcement. Do not remove. */
export const INTERNAL_ONLY = true as const;
export const INTERNAL_FACADE = "executionEngine.ts" as const;

// ─── Event Types ─────────────────────────────────────────────────────────────

export type AutomationEventType =
  | "lead_created"
  | "lead_assigned"
  | "lead_stage_changed"
  | "appointment_scheduled"
  | "quote_created"
  | "quote_sent"
  | "quote_won"
  | "quote_stale"
  | "job_created"
  | "job_delayed"
  | "job_missing_data"
  | "issue_created"
  | "sms_received"
  | "wrap_submitted"
  | "install_completed"
  | "qa_passed"
  | "payment_collected"
  | "payment_overdue";

export type AutomationActionType =
  | "send_notification"
  | "send_sms"
  | "create_reminder"
  | "update_flag"
  | "assign_task";

export const EVENT_TYPE_LABELS: Record<AutomationEventType, string> = {
  lead_created: "New Lead Created",
  lead_assigned: "Lead Assigned",
  lead_stage_changed: "Lead Stage Changed",
  appointment_scheduled: "Appointment Scheduled",
  quote_created: "Quote Created",
  quote_sent: "Quote Sent",
  quote_won: "Quote Won",
  quote_stale: "Quote Stale / No Response",
  job_created: "Job Created",
  job_delayed: "Job Delayed",
  job_missing_data: "Job Missing Data",
  issue_created: "Issue Created",
  sms_received: "Inbound SMS Received",
  wrap_submitted: "Installer Wrap Submitted",
  install_completed: "Install Completed",
  qa_passed: "QA Passed",
  payment_collected: "Payment Collected",
  payment_overdue: "Payment Overdue",
};

export const ACTION_TYPE_LABELS: Record<AutomationActionType, string> = {
  send_notification: "Send Internal Alert",
  send_sms: "Send SMS",
  create_reminder: "Create Follow-Up Reminder",
  update_flag: "Update Status Flag",
  assign_task: "Assign Task",
};

export const EVENT_MODULES: Record<AutomationEventType, string> = {
  lead_created: "pipeline",
  lead_assigned: "pipeline",
  lead_stage_changed: "pipeline",
  appointment_scheduled: "pipeline",
  quote_created: "quotes",
  quote_sent: "quotes",
  quote_won: "quotes",
  quote_stale: "quotes",
  job_created: "jobs",
  job_delayed: "jobs",
  job_missing_data: "jobs",
  issue_created: "issues",
  sms_received: "communication",
  wrap_submitted: "jobs",
  install_completed: "jobs",
  qa_passed: "jobs",
  payment_collected: "jobs",
  payment_overdue: "jobs",
};

// ─── Condition Types ─────────────────────────────────────────────────────────

export type ConditionField =
  | "lead_source"
  | "stage"
  | "severity"
  | "assigned_rep"
  | "project_type"
  | "city"
  | "delay_days";

export type ConditionOperator = "equals" | "not_equals" | "contains" | "greater_than" | "less_than" | "exists" | "not_exists";

export interface RuleCondition {
  id: string;
  field: ConditionField;
  operator: ConditionOperator;
  value: string;
}

export const CONDITION_FIELD_LABELS: Record<ConditionField, string> = {
  lead_source: "Lead Source",
  stage: "Pipeline Stage",
  severity: "Severity",
  assigned_rep: "Assigned Rep",
  project_type: "Project Type",
  city: "City",
  delay_days: "Delay Days",
};

export const CONDITION_OPERATOR_LABELS: Record<ConditionOperator, string> = {
  equals: "equals",
  not_equals: "does not equal",
  contains: "contains",
  greater_than: "is greater than",
  less_than: "is less than",
  exists: "exists",
  not_exists: "does not exist",
};

// ─── Core Types ──────────────────────────────────────────────────────────────

export interface AutomationEvent {
  id: string;
  type: AutomationEventType;
  timestamp: string;
  payload: Record<string, any>;
  sourceModule: "pipeline" | "quotes" | "jobs" | "communication" | "issues";
  entityId: string;
  entityName: string;
  triggeredBy: string;
}

export interface AutomationAction {
  id: string;
  type: AutomationActionType;
  description: string;
  target: string;
  status: "pending" | "completed" | "failed";
  executedAt?: string;
  metadata?: Record<string, any>;
}

export interface RuleActionDef {
  id: string;
  type: AutomationActionType;
  template: string;
  target: string;
  delayHours?: number;
}

export interface AutomationExecution {
  id: string;
  ruleId: string;
  ruleName: string;
  event: AutomationEvent;
  actions: AutomationAction[];
  executedAt: string;
  success: boolean;
}

export interface Reminder {
  id: string;
  title: string;
  description: string;
  dueAt: string;
  assignedTo: string;
  entityType: "lead" | "quote" | "job" | "contact";
  entityId: string;
  entityName: string;
  status: "pending" | "completed" | "dismissed";
  createdAt: string;
  createdBy: string;
  completedAt?: string;
  automationRuleId?: string;
}

export type RuleStatus = "active" | "inactive" | "archived";

export interface AutomationRule {
  id: string;
  name: string;
  description: string;
  category: "sales" | "quotes" | "operations" | "communication";
  eventType: AutomationEventType;
  conditions: RuleCondition[];
  actions: RuleActionDef[];
  status: RuleStatus;
  enabled: boolean;
  priority: number;
  // Audit
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  lastTriggeredAt?: string;
  lastTriggeredEntity?: string;
  triggerCount: number;
}

// ─── Target options ──────────────────────────────────────────────────────────

export const TARGET_OPTIONS = [
  { value: "assigned_rep", label: "Assigned Rep" },
  { value: "ops_team", label: "Operations Team" },
  { value: "admin", label: "Admin" },
  { value: "owner", label: "Owner" },
  { value: "all_reps", label: "All Sales Reps" },
];

// ─── Default Rules ───────────────────────────────────────────────────────────

const now = "2026-03-25T10:00:00Z";

export const DEFAULT_AUTOMATION_RULES: AutomationRule[] = [
  {
    id: "rule-001", name: "New Lead Notification", description: "Notify assigned rep when a new lead is created",
    category: "sales", eventType: "lead_created", conditions: [],
    actions: [{ id: "a1", type: "send_notification", template: "New lead assigned: {{entityName}}", target: "assigned_rep" }],
    status: "active", enabled: true, priority: 8,
    createdAt: now, createdBy: "System", updatedAt: now, updatedBy: "System", triggerCount: 5, lastTriggeredAt: "2026-03-27T08:15:00Z", lastTriggeredEntity: "Rachel Nguyen",
  },
  {
    id: "rule-002", name: "Lead Assignment Alert", description: "Notify rep and create follow-up when lead is assigned",
    category: "sales", eventType: "lead_assigned", conditions: [],
    actions: [
      { id: "a2", type: "send_notification", template: "Lead {{entityName}} has been assigned to you", target: "assigned_rep" },
      { id: "a3", type: "create_reminder", template: "Follow up with {{entityName}}", target: "assigned_rep", delayHours: 4 },
    ],
    status: "active", enabled: true, priority: 8,
    createdAt: now, createdBy: "System", updatedAt: now, updatedBy: "System", triggerCount: 8,
  },
  {
    id: "rule-003", name: "Appointment Confirmation", description: "Create reminder for appointment confirmation SMS",
    category: "sales", eventType: "appointment_scheduled", conditions: [],
    actions: [
      { id: "a4", type: "create_reminder", template: "Send appointment confirmation to {{entityName}}", target: "assigned_rep", delayHours: 1 },
      { id: "a5", type: "send_notification", template: "Appointment scheduled for {{entityName}}", target: "assigned_rep" },
    ],
    status: "active", enabled: true, priority: 7,
    createdAt: now, createdBy: "System", updatedAt: now, updatedBy: "System", triggerCount: 3, lastTriggeredAt: "2026-03-26T14:00:00Z", lastTriggeredEntity: "Kevin & Lisa Morales",
  },
  {
    id: "rule-004", name: "Stage Change Tracking", description: "Log and notify on pipeline stage changes",
    category: "sales", eventType: "lead_stage_changed", conditions: [],
    actions: [{ id: "a6", type: "send_notification", template: "{{entityName}} moved to {{newStage}}", target: "assigned_rep" }],
    status: "active", enabled: true, priority: 5,
    createdAt: now, createdBy: "System", updatedAt: now, updatedBy: "System", triggerCount: 12,
  },
  {
    id: "rule-005", name: "Quote Created Notification", description: "Notify when a new quote is created",
    category: "quotes", eventType: "quote_created", conditions: [],
    actions: [{ id: "a7", type: "send_notification", template: "Quote created for {{entityName}}", target: "assigned_rep" }],
    status: "active", enabled: true, priority: 6,
    createdAt: now, createdBy: "System", updatedAt: now, updatedBy: "System", triggerCount: 4,
  },
  {
    id: "rule-006", name: "Quote Follow-Up Reminder", description: "Create follow-up reminder after quote is sent",
    category: "quotes", eventType: "quote_sent", conditions: [],
    actions: [
      { id: "a8", type: "create_reminder", template: "Follow up on quote for {{entityName}}", target: "assigned_rep", delayHours: 24 },
      { id: "a9", type: "send_notification", template: "Quote sent to {{entityName}} — follow-up in 24h", target: "assigned_rep" },
    ],
    status: "active", enabled: true, priority: 7,
    createdAt: now, createdBy: "System", updatedAt: now, updatedBy: "System", triggerCount: 3, lastTriggeredAt: "2026-03-23T10:00:00Z", lastTriggeredEntity: "Frank Castellano",
  },
  {
    id: "rule-007", name: "Stale Quote Alert", description: "Alert rep when quote has no response within timeframe",
    category: "quotes", eventType: "quote_stale", conditions: [],
    actions: [
      { id: "a10", type: "send_notification", template: "Quote for {{entityName}} has not been responded to — action needed", target: "assigned_rep" },
      { id: "a11", type: "create_reminder", template: "Urgent: Follow up on stale quote for {{entityName}}", target: "assigned_rep" },
    ],
    status: "active", enabled: true, priority: 9,
    createdAt: now, createdBy: "System", updatedAt: now, updatedBy: "System", triggerCount: 1,
  },
  {
    id: "rule-008", name: "Quote Won — Job Creation", description: "Notify ops team when quote is won",
    category: "quotes", eventType: "quote_won", conditions: [],
    actions: [
      { id: "a12", type: "send_notification", template: "🎉 Quote won for {{entityName}} — job creation initiated", target: "ops_team" },
      { id: "a13", type: "send_notification", template: "Congratulations! Quote for {{entityName}} is sold", target: "assigned_rep" },
    ],
    status: "active", enabled: true, priority: 10,
    createdAt: now, createdBy: "System", updatedAt: now, updatedBy: "System", triggerCount: 2,
  },
  {
    id: "rule-009", name: "Job Created Notification", description: "Notify ops and install team when new job is created",
    category: "operations", eventType: "job_created", conditions: [],
    actions: [
      { id: "a14", type: "send_notification", template: "New job created: {{entityName}}", target: "ops_team" },
      { id: "a15", type: "create_reminder", template: "Review readiness for {{entityName}}", target: "ops_team", delayHours: 24 },
    ],
    status: "active", enabled: true, priority: 8,
    createdAt: now, createdBy: "System", updatedAt: now, updatedBy: "System", triggerCount: 6,
  },
  {
    id: "rule-010", name: "Job Delay Alert", description: "Priority alert when job is delayed",
    category: "operations", eventType: "job_delayed", conditions: [],
    actions: [
      { id: "a16", type: "send_notification", template: "⚠️ Job {{entityName}} is delayed — immediate attention required", target: "ops_team" },
      { id: "a17", type: "send_notification", template: "Job {{entityName}} is experiencing delays", target: "assigned_rep" },
    ],
    status: "active", enabled: true, priority: 10,
    createdAt: now, createdBy: "System", updatedAt: now, updatedBy: "System", triggerCount: 1, lastTriggeredAt: "2026-03-26T09:00:00Z", lastTriggeredEntity: "Torres Shower Remodel",
  },
  {
    id: "rule-011", name: "Missing Data Warning", description: "Alert when job is missing required data",
    category: "operations", eventType: "job_missing_data", conditions: [],
    actions: [{ id: "a18", type: "send_notification", template: "Job {{entityName}} is missing required data: {{missingItems}}", target: "ops_team" }],
    status: "active", enabled: true, priority: 9,
    createdAt: now, createdBy: "System", updatedAt: now, updatedBy: "System", triggerCount: 2,
  },
  {
    id: "rule-012", name: "Issue Created Alert", description: "Notify when a new issue is created",
    category: "operations", eventType: "issue_created", conditions: [],
    actions: [{ id: "a19", type: "send_notification", template: "New issue reported on {{entityName}}: {{issueTitle}}", target: "ops_team" }],
    status: "active", enabled: true, priority: 7,
    createdAt: now, createdBy: "System", updatedAt: now, updatedBy: "System", triggerCount: 3,
  },
  {
    id: "rule-013", name: "Inbound SMS Notification", description: "Notify assigned rep when SMS is received",
    category: "communication", eventType: "sms_received", conditions: [],
    actions: [{ id: "a20", type: "send_notification", template: "New SMS from {{entityName}}", target: "assigned_rep" }],
    status: "active", enabled: true, priority: 8,
    createdAt: now, createdBy: "System", updatedAt: now, updatedBy: "System", triggerCount: 7,
  },
  {
    id: "rule-014", name: "Wrap Submitted — Ops Review", description: "Notify ops when installer submits daily wrap",
    category: "operations", eventType: "wrap_submitted", conditions: [],
    actions: [
      { id: "a21", type: "send_notification", template: "Installer wrap submitted for {{entityName}}", target: "ops_team" },
    ],
    status: "active", enabled: true, priority: 7,
    createdAt: now, createdBy: "System", updatedAt: now, updatedBy: "System", triggerCount: 0,
  },
  {
    id: "rule-015", name: "Install Completed — QA Ready", description: "Notify QA team when install is completed",
    category: "operations", eventType: "install_completed", conditions: [],
    actions: [
      { id: "a22", type: "send_notification", template: "🎉 Install completed for {{entityName}} — QA review required", target: "ops_team" },
      { id: "a23", type: "create_reminder", template: "Review QA for {{entityName}}", target: "ops_team", delayHours: 2 },
    ],
    status: "active", enabled: true, priority: 9,
    createdAt: now, createdBy: "System", updatedAt: now, updatedBy: "System", triggerCount: 0,
  },
  {
    id: "rule-016", name: "QA Passed — Closeout Ready", description: "Notify ops when QA passes",
    category: "operations", eventType: "qa_passed", conditions: [],
    actions: [
      { id: "a24", type: "send_notification", template: "QA passed for {{entityName}} — ready for closeout", target: "ops_team" },
    ],
    status: "active", enabled: true, priority: 8,
    createdAt: now, createdBy: "System", updatedAt: now, updatedBy: "System", triggerCount: 0,
  },
  {
    id: "rule-017", name: "Payment Collected Confirmation", description: "Log when payment is collected in the field",
    category: "operations", eventType: "payment_collected", conditions: [],
    actions: [
      { id: "a25", type: "send_notification", template: "💰 Payment collected for {{entityName}}", target: "ops_team" },
    ],
    status: "active", enabled: true, priority: 8,
    createdAt: now, createdBy: "System", updatedAt: now, updatedBy: "System", triggerCount: 0,
  },
  {
    id: "rule-018", name: "Payment Overdue Alert", description: "Alert when payment is overdue",
    category: "operations", eventType: "payment_overdue", conditions: [],
    actions: [
      { id: "a26", type: "send_notification", template: "⚠️ Payment overdue for {{entityName}} — action required", target: "ops_team" },
      { id: "a27", type: "create_reminder", template: "Follow up on overdue payment for {{entityName}}", target: "ops_team", delayHours: 24 },
    ],
    status: "active", enabled: true, priority: 10,
    createdAt: now, createdBy: "System", updatedAt: now, updatedBy: "System", triggerCount: 0,
  },
];

// ─── Condition Evaluation ────────────────────────────────────────────────────

export function evaluateConditions(
  conditions: RuleCondition[],
  payload: Record<string, any>
): boolean {
  if (conditions.length === 0) return true;
  return conditions.every((cond) => {
    const val = payload[cond.field] ?? payload[cond.field.replace(/_/g, "")] ?? "";
    const strVal = String(val).toLowerCase();
    const condVal = cond.value.toLowerCase();
    switch (cond.operator) {
      case "equals": return strVal === condVal;
      case "not_equals": return strVal !== condVal;
      case "contains": return strVal.includes(condVal);
      case "greater_than": return parseFloat(strVal) > parseFloat(condVal);
      case "less_than": return parseFloat(strVal) < parseFloat(condVal);
      case "exists": return !!val && strVal !== "";
      case "not_exists": return !val || strVal === "";
      default: return true;
    }
  });
}

// ─── Automation Engine ───────────────────────────────────────────────────────

export function processAutomationEvent(
  event: AutomationEvent,
  rules: AutomationRule[]
): AutomationExecution[] {
  const ts = new Date().toISOString();
  const matchingRules = rules
    .filter((r) => r.enabled && r.status === "active" && r.eventType === event.type)
    .filter((r) => evaluateConditions(r.conditions, event.payload))
    .sort((a, b) => b.priority - a.priority);

  return matchingRules.map((rule) => {
    const actions: AutomationAction[] = rule.actions.map((actionDef, idx) => {
      let description = actionDef.template;
      Object.entries(event.payload).forEach(([key, value]) => {
        description = description.replace(`{{${key}}}`, String(value));
      });
      description = description.replace("{{entityName}}", event.entityName);
      description = description.replace("{{entityId}}", event.entityId);
      return {
        id: `act-${Date.now()}-${idx}`,
        type: actionDef.type,
        description,
        target: actionDef.target === "assigned_rep"
          ? (event.payload.assignedRep || event.triggeredBy)
          : actionDef.target === "ops_team" ? "Operations Team" : actionDef.target,
        status: "completed" as const,
        executedAt: ts,
        metadata: actionDef.delayHours ? { delayHours: actionDef.delayHours } : undefined,
      };
    });
    return { id: `exec-${Date.now()}-${rule.id}`, ruleId: rule.id, ruleName: rule.name, event, actions, executedAt: ts, success: true };
  });
}

export function createReminderFromAction(action: AutomationAction, event: AutomationEvent, ruleId: string): Reminder {
  const delayHours = action.metadata?.delayHours || 24;
  return {
    id: `rem-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    title: action.description,
    description: `Auto-created from ${event.type} event`,
    dueAt: new Date(Date.now() + delayHours * 3600000).toISOString(),
    assignedTo: action.target,
    entityType: event.sourceModule === "pipeline" ? "lead" : event.sourceModule === "quotes" ? "quote" : event.sourceModule === "jobs" ? "job" : "contact",
    entityId: event.entityId,
    entityName: event.entityName,
    status: "pending",
    createdAt: new Date().toISOString(),
    createdBy: "Automation Engine",
    automationRuleId: ruleId,
  };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function createEmptyRule(): AutomationRule {
  const ts = new Date().toISOString();
  return {
    id: `rule-${Date.now()}`,
    name: "",
    description: "",
    category: "sales",
    eventType: "lead_created",
    conditions: [],
    actions: [{ id: `a-${Date.now()}`, type: "send_notification", template: "", target: "assigned_rep" }],
    status: "active",
    enabled: true,
    priority: 5,
    createdAt: ts,
    createdBy: "Admin",
    updatedAt: ts,
    updatedBy: "Admin",
    triggerCount: 0,
  };
}

export function duplicateRule(rule: AutomationRule): AutomationRule {
  const ts = new Date().toISOString();
  return {
    ...rule,
    id: `rule-${Date.now()}`,
    name: `${rule.name} (Copy)`,
    conditions: rule.conditions.map((c) => ({ ...c, id: `cond-${Date.now()}-${Math.random().toString(36).slice(2, 5)}` })),
    actions: rule.actions.map((a) => ({ ...a, id: `a-${Date.now()}-${Math.random().toString(36).slice(2, 5)}` })),
    createdAt: ts,
    createdBy: "Admin",
    updatedAt: ts,
    updatedBy: "Admin",
    triggerCount: 0,
    lastTriggeredAt: undefined,
    lastTriggeredEntity: undefined,
  };
}

// ─── Seed Data ───────────────────────────────────────────────────────────────

export const seedReminders: Reminder[] = [
  { id: "rem-001", title: "Follow up with Angela Torres", description: "Auto-created from lead_stage_changed event", dueAt: "2026-03-27T16:00:00Z", assignedTo: "Wesley Woodfin", entityType: "lead", entityId: "LEAD-003", entityName: "Angela Torres", status: "pending", createdAt: "2026-03-26T12:00:00Z", createdBy: "Automation Engine", automationRuleId: "rule-002" },
  { id: "rem-002", title: "Follow up on quote for Frank Castellano", description: "Auto-created from quote_sent event", dueAt: "2026-03-28T10:00:00Z", assignedTo: "Trent Hill", entityType: "quote", entityId: "QTE-002", entityName: "Frank Castellano", status: "pending", createdAt: "2026-03-24T10:00:00Z", createdBy: "Automation Engine", automationRuleId: "rule-006" },
  { id: "rem-003", title: "Send appointment confirmation to Kevin & Lisa Morales", description: "Auto-created from appointment_scheduled event", dueAt: "2026-03-29T09:00:00Z", assignedTo: "Lucas Schall", entityType: "lead", entityId: "LEAD-004", entityName: "Kevin & Lisa Morales", status: "pending", createdAt: "2026-03-26T14:00:00Z", createdBy: "Automation Engine", automationRuleId: "rule-003" },
  { id: "rem-004", title: "Review readiness for Rodriguez Shower Remodel", description: "Auto-created from job_created event", dueAt: "2026-03-27T08:00:00Z", assignedTo: "Operations Team", entityType: "job", entityId: "JOB-001", entityName: "Rodriguez Shower Remodel", status: "completed", createdAt: "2026-03-15T08:00:00Z", createdBy: "Automation Engine", completedAt: "2026-03-16T10:00:00Z", automationRuleId: "rule-009" },
  { id: "rem-005", title: "Urgent: Follow up on stale quote for Susan Blackwell", description: "Auto-created from quote_stale event", dueAt: "2026-03-27T14:00:00Z", assignedTo: "Trent Hill", entityType: "lead", entityId: "LEAD-005", entityName: "Susan Blackwell", status: "pending", createdAt: "2026-03-27T08:00:00Z", createdBy: "Automation Engine", automationRuleId: "rule-007" },
];

export const seedExecutions: AutomationExecution[] = [
  { id: "exec-001", ruleId: "rule-001", ruleName: "New Lead Notification", event: { id: "evt-001", type: "lead_created", timestamp: "2026-03-27T08:15:00Z", payload: { assignedRep: "Lucas Schall" }, sourceModule: "pipeline", entityId: "LEAD-007", entityName: "Rachel Nguyen", triggeredBy: "System" }, actions: [{ id: "act-001", type: "send_notification", description: "New lead assigned: Rachel Nguyen", target: "Lucas Schall", status: "completed", executedAt: "2026-03-27T08:15:01Z" }], executedAt: "2026-03-27T08:15:01Z", success: true },
  { id: "exec-002", ruleId: "rule-003", ruleName: "Appointment Confirmation", event: { id: "evt-002", type: "appointment_scheduled", timestamp: "2026-03-26T14:00:00Z", payload: { assignedRep: "Lucas Schall" }, sourceModule: "pipeline", entityId: "LEAD-004", entityName: "Kevin & Lisa Morales", triggeredBy: "Lucas Schall" }, actions: [{ id: "act-002", type: "create_reminder", description: "Send appointment confirmation to Kevin & Lisa Morales", target: "Lucas Schall", status: "completed", executedAt: "2026-03-26T14:00:01Z" }, { id: "act-003", type: "send_notification", description: "Appointment scheduled for Kevin & Lisa Morales", target: "Lucas Schall", status: "completed", executedAt: "2026-03-26T14:00:01Z" }], executedAt: "2026-03-26T14:00:01Z", success: true },
  { id: "exec-003", ruleId: "rule-006", ruleName: "Quote Follow-Up Reminder", event: { id: "evt-003", type: "quote_sent", timestamp: "2026-03-23T10:00:00Z", payload: { assignedRep: "Trent Hill" }, sourceModule: "quotes", entityId: "QTE-002", entityName: "Frank Castellano", triggeredBy: "Trent Hill" }, actions: [{ id: "act-004", type: "create_reminder", description: "Follow up on quote for Frank Castellano", target: "Trent Hill", status: "completed", executedAt: "2026-03-23T10:00:01Z" }, { id: "act-005", type: "send_notification", description: "Quote sent to Frank Castellano — follow-up in 24h", target: "Trent Hill", status: "completed", executedAt: "2026-03-23T10:00:01Z" }], executedAt: "2026-03-23T10:00:01Z", success: true },
  { id: "exec-004", ruleId: "rule-010", ruleName: "Job Delay Alert", event: { id: "evt-004", type: "job_delayed", timestamp: "2026-03-26T09:00:00Z", payload: { assignedRep: "Wesley Woodfin", reason: "Material shortage" }, sourceModule: "jobs", entityId: "JOB-003", entityName: "Torres Shower Remodel", triggeredBy: "System" }, actions: [{ id: "act-006", type: "send_notification", description: "⚠️ Job Torres Shower Remodel is delayed — immediate attention required", target: "Operations Team", status: "completed", executedAt: "2026-03-26T09:00:01Z" }, { id: "act-007", type: "send_notification", description: "Job Torres Shower Remodel is experiencing delays", target: "Wesley Woodfin", status: "completed", executedAt: "2026-03-26T09:00:01Z" }], executedAt: "2026-03-26T09:00:01Z", success: true },
];
